import React, { useState, useEffect } from 'react';
import { 
  Table, Button, Modal, Form, Input, Space, Tag, message, Popconfirm, Card, Row, Col, Select
} from 'antd';
import { 
  PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, MailOutlined
} from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import { getDepartments, createDepartment, updateDepartment, deleteDepartment } from '../services/api';

const { Option } = Select;

const DepartmentManagement = () => {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [searchText, setSearchText] = useState('');
  useAuth();

  const fetchDepartments = async () => {
    try {
      setLoading(true);
      const data = await getDepartments();
      setDepartments(data);
    } catch (error) {
      console.error('Error fetching departments:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to load departments';
      const errorDetails = error.response?.data?.error;
      console.error('Error details:', {
        status: error.response?.status,
        message: errorMessage,
        details: errorDetails,
        response: error.response?.data
      });
      
      if (error.response?.status === 403) {
        message.error('You do not have permission to view departments');
      } else if (error.response?.status === 401) {
        message.error('Authentication required. Please log in again.');
      } else if (error.response?.status === 404) {
        message.error('Departments API endpoint not found. Please check if the server is running.');
      } else {
        message.error(`Failed to load departments: ${errorMessage}`);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDepartments();
  }, []);

  // Departments are global/common across regions; no region selection needed.

  const handleSubmit = async (values) => {
    try {
      const departmentData = {
        name: values.name,
        description: values.description || null,
        email: values.email || null
      };

      if (editingDepartment) {
        await updateDepartment(editingDepartment.id, departmentData);
        message.success('Department updated successfully');
      } else {
        await createDepartment(departmentData);
        message.success('Department created successfully');
      }

      setIsModalVisible(false);
      form.resetFields();
      setEditingDepartment(null);
      fetchDepartments();
    } catch (error) {
      console.error('Error saving department:', error);
      message.error(error.response?.data?.message || 'Failed to save department');
    }
  };

  const handleEdit = (department) => {
    setEditingDepartment(department);
    form.setFieldsValue({
      name: department.name,
      description: department.description || '',
      email: department.email || ''
    });
    setIsModalVisible(true);
  };

  const handleDelete = async (departmentId) => {
    try {
      await deleteDepartment(departmentId);
      message.success('Department deleted successfully');
      fetchDepartments();
    } catch (error) {
      console.error('Error deleting department:', error);
      message.error(error.response?.data?.message || 'Failed to delete department');
    }
  };

  const filteredDepartments = departments.filter(dept => 
    dept.name.toLowerCase().includes(searchText.toLowerCase()) ||
    dept.description?.toLowerCase().includes(searchText.toLowerCase()) ||
    dept.email?.toLowerCase().includes(searchText.toLowerCase())
  );

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (text) => text || <span style={{ color: '#999' }}>No description</span>
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (text) => text ? (
        <Space>
          <MailOutlined />
          <span>{text}</span>
        </Space>
      ) : (
        <span style={{ color: '#999' }}>No email</span>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space size="middle">
          <Button 
            type="text" 
            icon={<EditOutlined />} 
            onClick={() => handleEdit(record)}
            title="Edit"
          />
          <Popconfirm
            title="Are you sure you want to delete this department?"
            description="This action cannot be undone. Make sure no users or contracts are assigned to this department."
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button 
              type="text" 
              danger 
              icon={<DeleteOutlined />} 
              title="Delete"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="department-management">
      <Card 
        title="Department Management"
        extra={
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={() => {
              setEditingDepartment(null);
              form.resetFields();
              setIsModalVisible(true);
            }}
          >
            Add Department
          </Button>
        }
      >
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} md={12} lg={8}>
            <Input
              placeholder="Search departments..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
        </Row>

        <Table 
          columns={columns} 
          dataSource={filteredDepartments} 
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          scroll={{ x: true }}
        />
      </Card>

      <Modal
        title={editingDepartment ? 'Edit Department' : 'Add New Department'}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
          setEditingDepartment(null);
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="name"
            label="Department Name"
            rules={[
              { required: true, message: 'Please enter a department name' },
              { min: 2, message: 'Department name must be at least 2 characters' },
              { max: 100, message: 'Department name cannot exceed 100 characters' }
            ]}
          >
            <Input placeholder="Enter department name" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[
              { max: 255, message: 'Description cannot exceed 255 characters' }
            ]}
          >
            <Input.TextArea rows={3} placeholder="Enter description" />
          </Form.Item>

          <Form.Item
            name="email"
            label="Notification Email"
            help="This email will be used for receiving contract expiry notifications for all contracts in this department."
            rules={[
              { type: 'email', message: 'Please enter a valid email address' }
            ]}
          >
            <Input 
              type="email"
              placeholder="Enter notification email (optional)"
              prefix={<MailOutlined />}
            />
          </Form.Item>

          {/* Departments are global/common across regions; region selection removed */}

          <Form.Item style={{ marginTop: 32, marginBottom: 0 }}>
            <Space>
              <Button 
                type="primary" 
                htmlType="submit"
              >
                {editingDepartment ? 'Update' : 'Create'} Department
              </Button>
              <Button 
                onClick={() => {
                  setIsModalVisible(false);
                  form.resetFields();
                  setEditingDepartment(null);
                }}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default DepartmentManagement;

